import React, { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Job Spec Match v2 — persistent specs, AI judgment scoring.
 *
 * Flow: pick/create a spec (paste text, fetch a job URL, or pull a vacancy
 * from the project's job watches) → review the AI breakdown (criteria with
 * weights) → score all enriched candidates → ranked results with per-criterion
 * breakdowns, live weight re-ranking (no re-scoring cost) and optional prose
 * rationales for the shortlist. "Apply to table" pushes the ranking into the
 * data table's Match column.
 */

export interface JsCriterion {
  id: string;
  label: string;
  bucket: 'must' | 'nice';
  weight: number;
  instructions: string;
  levels: string[];
}

export interface JsSpec {
  id: number;
  title: string;
  source: string;
  status: string;
  error?: string | null;
  scored_count: number;
  total_count: number;
  criteria: JsCriterion[];
  updated_at?: string;
}

export interface JsResult {
  candidate_id: number;
  name: string;
  headline?: string | null;
  current_position?: string | null;
  current_company?: string | null;
  composite: number;
  scores: Record<string, { score: number; confidence?: number }>;
  rationale?: string | null;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  projectId: number;
  onApplyToTable: (spec: JsSpec, results: JsResult[]) => void;
  // When set (Jobs-tab handoff), the modal opens straight onto this spec
  // (review view, or results if it's already scored) instead of the list.
  initialSpecId?: number | null;
}

const authHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
});

export function computeComposite(
  scores: Record<string, { score: number }>, criteria: JsCriterion[]
): number {
  let sum = 0, totalWeight = 0;
  for (const crit of criteria) {
    const w = crit.weight || 0;
    if (w <= 0) continue;
    const raw = scores && scores[crit.id] ? scores[crit.id].score : 0;
    sum += w * (raw / Math.max(1, crit.levels.length - 1));
    totalWeight += w;
  }
  return totalWeight > 0 ? Math.round(1000 * sum / totalWeight) / 10 : 0;
}

const scoreBadgeClass = (v: number) =>
  v >= 75 ? 'bg-green-100 text-green-800' :
  v >= 55 ? 'bg-yellow-100 text-yellow-800' :
  v >= 35 ? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-600';

/**
 * Match column click-through: one candidate's breakdown against the applied
 * spec — per-criterion bars, the prose rationale if written, and a
 * write-it-now button if not. Rendered fixed at the click position.
 */
export const MatchBreakdownPopover: React.FC<{
  specId: number;
  candidateId: number;
  candidateName?: string;
  position: { x: number; y: number };
  onClose: () => void;
}> = ({ specId, candidateId, candidateName, position, onClose }) => {
  const [detail, setDetail] = useState<{
    title: string; criteria: JsCriterion[]; scores: Record<string, { score: number }>;
    rationale: string | null; composite: number;
  } | null>(null);
  const [failed, setFailed] = useState(false);
  const [writing, setWriting] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`/api/job-specs/${specId}/candidates/${candidateId}`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setDetail(data);
    } catch (e) {
      setFailed(true);
    }
  }, [specId, candidateId]);

  useEffect(() => { load(); }, [load]);

  const writeRationale = async () => {
    setWriting(true);
    try {
      await fetch(`/api/job-specs/${specId}/rationales`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ candidateIds: [candidateId] }),
      });
      await load();
    } catch (e) { /* shown as still-missing */ } finally {
      setWriting(false);
    }
  };

  // Keep the popover on-screen: clamp x, open upward if near the bottom.
  const width = 340;
  const left = Math.max(8, Math.min(position.x, (window.innerWidth || 1200) - width - 8));
  const openUp = position.y > (window.innerHeight || 800) * 0.55;

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div
        className="fixed z-50 bg-white rounded-xl shadow-2xl border border-gray-200 p-3"
        style={{ left, width, ...(openUp ? { bottom: (window.innerHeight || 800) - position.y + 8 } : { top: position.y + 8 }), maxHeight: '60vh', overflowY: 'auto' }}
        onClick={e => e.stopPropagation()}
      >
        {failed ? (
          <p className="text-xs text-gray-500 p-2">Couldn't load the match detail for this candidate.</p>
        ) : !detail ? (
          <p className="text-xs text-gray-400 p-2">Loading…</p>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                {candidateName && <div className="text-sm font-semibold text-gray-900 truncate">{candidateName}</div>}
                <div className="text-[11px] text-gray-500 truncate" title={detail.title}>vs {detail.title}</div>
              </div>
              <span className={`px-2 py-0.5 rounded text-sm font-semibold ${scoreBadgeClass(detail.composite)}`}>
                {detail.composite}
              </span>
            </div>
            {detail.rationale ? (
              <p className="text-xs text-gray-700 bg-amber-50 border border-amber-100 rounded p-2">{detail.rationale}</p>
            ) : (
              <button
                onClick={writeRationale}
                disabled={writing}
                className="w-full text-xs font-medium border border-amber-200 text-amber-700 bg-amber-50 hover:bg-amber-100 rounded p-1.5 disabled:opacity-60"
              >
                {writing ? 'Writing…' : '✨ Write an explanation for this candidate'}
              </button>
            )}
            {detail.criteria.filter(c => (c.weight || 0) > 0).map(c => {
              const s = detail.scores[c.id];
              const norm = s ? s.score / Math.max(1, c.levels.length - 1) : 0;
              const levelText = s ? c.levels[Math.min(c.levels.length - 1, Math.round(s.score))] : 'No score';
              return (
                <div key={c.id}>
                  <div className="flex justify-between text-[11px] text-gray-600">
                    <span className="truncate pr-2">{c.label}</span>
                    <span className="flex-shrink-0">{s ? s.score.toFixed(1) : '—'}/{c.levels.length - 1}</span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${norm >= 0.7 ? 'bg-green-400' : norm >= 0.4 ? 'bg-yellow-400' : 'bg-orange-300'}`}
                      style={{ width: `${Math.round(100 * norm)}%` }}
                    />
                  </div>
                  <div className="text-[10px] text-gray-400 truncate" title={levelText}>{levelText}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export const JobSpecMatchV2Modal: React.FC<Props> = ({ isOpen, onClose, projectId, onApplyToTable, initialSpecId }) => {
  const [view, setView] = useState<'list' | 'create' | 'review' | 'results'>('list');
  const [specs, setSpecs] = useState<JsSpec[]>([]);
  const [spec, setSpec] = useState<JsSpec | null>(null);
  const [results, setResults] = useState<JsResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // create view
  const [createTab, setCreateTab] = useState<'paste' | 'url' | 'job'>('paste');
  const [specText, setSpecText] = useState('');
  const [specUrl, setSpecUrl] = useState('');
  const [jobs, setJobs] = useState<Array<{ id: number; job_title: string; company_name: string }>>([]);
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);

  // results view
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [rationaleBusy, setRationaleBusy] = useState(false);
  const [showWeights, setShowWeights] = useState(false);
  const [rescoreBusy, setRescoreBusy] = useState(false);
  const [rescoreDone, setRescoreDone] = useState(false);
  const [weightsSaved, setWeightsSaved] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle');
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = () => {
    if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; }
  };

  const loadSpecs = useCallback(async () => {
    try {
      const res = await fetch(`/api/job-specs?projectId=${projectId}`, { headers: authHeaders() });
      const data = await res.json();
      if (res.ok) setSpecs(data.specs || []);
    } catch (e) { /* list load is non-critical */ }
  }, [projectId]);

  useEffect(() => {
    if (isOpen) {
      setView('list'); setSpec(null); setResults([]); setError(null);
      setSpecText(''); setSpecUrl(''); setSelectedJobId(null); setExpandedId(null);
      setShowWeights(false);
      loadSpecs();
      // Jobs-tab handoff: skip the list and open straight onto the spec.
      if (initialSpecId) {
        (async () => {
          try {
            const res = await fetch(`/api/job-specs/${initialSpecId}`, { headers: authHeaders() });
            const data = await res.json();
            if (res.ok && data.spec) {
              if (data.spec.status === 'ready') {
                await loadResults(data.spec.id);
              } else {
                setSpec(data.spec);
                setView('review');
                if (data.spec.status === 'scoring') beginPolling(data.spec.id);
              }
            }
          } catch (e) { /* fall back to the list view */ }
        })();
      }
    }
    return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, loadSpecs, initialSpecId]);

  const loadJobs = async () => {
    try {
      const res = await fetch(`/api/jobs/${projectId}?limit=100`, { headers: authHeaders() });
      const data = await res.json();
      if (res.ok) setJobs((data.jobs || []).filter((j: any) => j.job_description && j.job_description.length > 100));
    } catch (e) { /* job list optional */ }
  };

  const loadResults = async (specId: number) => {
    setLoading(true);
    try {
      const res = await fetch(`/api/job-specs/${specId}/results`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load results');
      setSpec(data.spec);
      setResults(data.results || []);
      setView('results');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const openSpec = async (s: JsSpec) => {
    setError(null);
    if (s.status === 'ready') { await loadResults(s.id); return; }
    // draft / failed / mid-scoring → load full spec into review
    try {
      const res = await fetch(`/api/job-specs/${s.id}`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load spec');
      setSpec(data.spec);
      setView('review');
      if (data.spec.status === 'scoring') beginPolling(data.spec.id);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const createSpec = async () => {
    setLoading(true); setError(null);
    try {
      const body: any = { projectId };
      if (createTab === 'paste') body.text = specText;
      else if (createTab === 'url') body.url = specUrl;
      else body.jobId = selectedJobId;
      const res = await fetch('/api/job-specs', {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to analyse the spec');
      setSpec(data.spec);
      setView('review');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const saveSpec = async (updated: JsSpec): Promise<boolean> => {
    setSpec(updated);
    try {
      const res = await fetch(`/api/job-specs/${updated.id}`, {
        method: 'PUT', headers: authHeaders(),
        body: JSON.stringify({ title: updated.title, criteria: updated.criteria }),
      });
      return res.ok;
    } catch (e) {
      return false;
    }
  };

  const saveWeightsExplicit = async () => {
    if (!spec) return;
    setWeightsSaved('saving');
    const ok = await saveSpec(spec);
    setWeightsSaved(ok ? 'saved' : 'failed');
    setTimeout(() => setWeightsSaved('idle'), 2500);
  };

  const beginPolling = (specId: number) => {
    stopPolling();
    pollTimer.current = setInterval(async () => {
      try {
        const res = await fetch(`/api/job-specs/${specId}`, { headers: authHeaders() });
        const data = await res.json();
        if (!res.ok) return;
        setSpec(data.spec);
        if (data.spec.status === 'ready') {
          stopPolling();
          await loadResults(specId);
          setRescoreBusy(false);
          setRescoreDone(true);
          setTimeout(() => setRescoreDone(false), 2500);
        } else if (data.spec.status === 'failed') {
          stopPolling();
          setRescoreBusy(false);
          setError(data.spec.error || 'Scoring failed');
        }
      } catch (e) { /* keep polling */ }
    }, 2000);
  };

  const startScoring = async () => {
    if (!spec) return;
    setError(null);
    if (view === 'results') setRescoreBusy(true); // feedback while staying on the results view
    await saveSpec(spec); // persist any pending edits first
    try {
      const res = await fetch(`/api/job-specs/${spec.id}/score`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to start scoring');
      setSpec({ ...spec, status: 'scoring' });
      beginPolling(spec.id);
    } catch (e: any) {
      setRescoreBusy(false);
      setError(e.message);
    }
  };

  const writeRationales = async () => {
    if (!spec) return;
    setRationaleBusy(true);
    try {
      const ids = ranked.slice(0, 15).map(r => r.candidate_id);
      await fetch(`/api/job-specs/${spec.id}/rationales`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ candidateIds: ids }),
      });
      await loadResults(spec.id);
    } catch (e) { /* non-fatal */ } finally {
      setRationaleBusy(false);
    }
  };

  const setWeight = (critId: string, weight: number) => {
    if (!spec) return;
    const updated = {
      ...spec,
      criteria: spec.criteria.map(c => c.id === critId ? { ...c, weight } : c),
    };
    setSpec(updated);
  };

  // Live re-ranking: composites recomputed from stored raw judgments — free.
  const ranked = React.useMemo(() => {
    if (!spec) return results;
    return results
      .map(r => ({ ...r, composite: computeComposite(r.scores, spec.criteria) }))
      .sort((a, b) => b.composite - a.composite);
  }, [results, spec]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xl">📋</span>
            <h2 className="text-lg font-semibold text-gray-900">
              {view === 'list' && 'Job Spec Match'}
              {view === 'create' && 'New job spec'}
              {view === 'review' && (spec ? spec.title : 'Review criteria')}
              {view === 'results' && (spec ? spec.title : 'Results')}
            </h2>
            <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-medium">AI</span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>

        {error && (
          <div className="mx-6 mt-3 px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded">
            {error}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* ---------------- LIST ---------------- */}
          {view === 'list' && (
            <div className="space-y-3">
              <button
                onClick={() => { setView('create'); setError(null); }}
                className="w-full py-3 border-2 border-dashed border-orange-300 rounded-lg text-orange-600 hover:bg-orange-50 font-medium"
              >
                + New job spec
              </button>
              {specs.length === 0 && (
                <p className="text-sm text-gray-500 text-center pt-2">
                  No job specs yet for this project. Create one from pasted text, a job advert URL, or a vacancy from your job watches.
                </p>
              )}
              {specs.map(s => (
                <button
                  key={s.id}
                  onClick={() => openSpec(s)}
                  className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:border-orange-300 hover:bg-orange-50/40 flex items-center justify-between gap-3"
                >
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900 truncate">{s.title}</div>
                    <div className="text-xs text-gray-500">
                      {s.source === 'job' ? 'From job watch' : s.source === 'url' ? 'From URL' : 'Pasted spec'}
                      {s.status === 'ready' && ` · ${s.scored_count} candidates scored`}
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded whitespace-nowrap ${
                    s.status === 'ready' ? 'bg-green-100 text-green-700' :
                    s.status === 'scoring' ? 'bg-blue-100 text-blue-700' :
                    s.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {s.status === 'ready' ? 'Ready' : s.status === 'scoring' ? 'Scoring…' : s.status === 'failed' ? 'Failed' : 'Draft'}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* ---------------- CREATE ---------------- */}
          {view === 'create' && (
            <div className="space-y-4">
              <div className="flex gap-2">
                {([['paste', 'Paste spec'], ['url', 'From URL'], ['job', 'From job watch']] as Array<['paste' | 'url' | 'job', string]>).map(([tab, label]) => (
                  <button
                    key={tab}
                    onClick={() => { setCreateTab(tab); if (tab === 'job' && jobs.length === 0) loadJobs(); }}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium ${createTab === tab
                      ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {createTab === 'paste' && (
                <textarea
                  value={specText}
                  onChange={e => setSpecText(e.target.value)}
                  placeholder="Paste the full job specification here…"
                  className="w-full h-64 border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                />
              )}
              {createTab === 'url' && (
                <div>
                  <input
                    value={specUrl}
                    onChange={e => setSpecUrl(e.target.value)}
                    placeholder="https://… (job advert URL)"
                    className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Works with most job boards and company career pages. If a page needs a login (some LinkedIn adverts), paste the text instead.
                  </p>
                </div>
              )}
              {createTab === 'job' && (
                <div>
                  {jobs.length === 0 ? (
                    <p className="text-sm text-gray-500">No vacancies with descriptions found in this project's job watches.</p>
                  ) : (
                    <select
                      value={selectedJobId ?? ''}
                      onChange={e => setSelectedJobId(parseInt(e.target.value) || null)}
                      className="w-full border border-gray-300 rounded-lg p-3 text-sm"
                    >
                      <option value="">Select a vacancy…</option>
                      {jobs.map(j => (
                        <option key={j.id} value={j.id}>{j.job_title} — {j.company_name}</option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              <div className="flex justify-between">
                <button onClick={() => setView('list')} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">← Back</button>
                <button
                  onClick={createSpec}
                  disabled={loading || (createTab === 'paste' ? specText.trim().length < 100 : createTab === 'url' ? !specUrl.trim() : !selectedJobId)}
                  className="px-5 py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-medium text-sm disabled:opacity-50"
                >
                  {loading ? 'Analysing spec…' : 'Analyse spec →'}
                </button>
              </div>
            </div>
          )}

          {/* ---------------- REVIEW ---------------- */}
          {view === 'review' && spec && (
            <div className="space-y-4">
              {spec.status === 'scoring' ? (
                <div className="text-center py-8">
                  <div className="text-3xl mb-3">⚡</div>
                  <p className="font-medium text-gray-800">Scoring candidates…</p>
                  <p className="text-sm text-gray-500 mt-1">{spec.scored_count} of {spec.total_count}</p>
                  <div className="w-64 h-2 bg-gray-200 rounded-full mx-auto mt-3 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-orange-400 to-amber-400 transition-all"
                      style={{ width: `${spec.total_count ? Math.round(100 * spec.scored_count / spec.total_count) : 0}%` }}
                    />
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-sm text-gray-600">
                    The spec was broken into the scoring criteria below. Adjust weights (or switch a criterion off), tweak wording if needed, then score the project's candidates (anyone with a profile: enriched or imported from the universe).
                  </p>
                  <input
                    value={spec.title}
                    onChange={e => setSpec({ ...spec, title: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-medium"
                  />
                  <div className="space-y-2">
                    {spec.criteria.map(c => (
                      <div key={c.id} className="border border-gray-200 rounded-lg px-3 py-2">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${c.bucket === 'must' ? 'bg-red-50 text-red-600' : 'bg-blue-50 text-blue-600'}`}>
                              {c.bucket === 'must' ? 'Must' : 'Nice'}
                            </span>
                            <span className="font-medium text-sm text-gray-800 truncate">{c.label}</span>
                          </div>
                          <select
                            value={c.weight}
                            onChange={e => setWeight(c.id, parseInt(e.target.value))}
                            className="text-xs border border-gray-300 rounded px-1.5 py-1"
                            title="Weight in the overall match score (Off excludes it)"
                          >
                            <option value={0}>Off</option>
                            <option value={1}>Weight 1</option>
                            <option value={2}>Weight 2</option>
                            <option value={3}>Weight 3</option>
                          </select>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{c.instructions}</p>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between items-center">
                    <button onClick={() => { setView('list'); loadSpecs(); }} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">← Back</button>
                    <button
                      onClick={startScoring}
                      className="px-5 py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-medium text-sm"
                    >
                      ⚡ Score candidates
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ---------------- RESULTS ---------------- */}
          {view === 'results' && spec && (
            <div className="space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-sm text-gray-600">
                  {ranked.length > 0
                    ? `${ranked.length} candidates ranked · adjust weights to re-rank instantly`
                    : spec.total_count === 0
                      ? 'No candidates with a profile to judge yet — this project has no enriched or universe-imported people. Add or enrich candidates, then re-score.'
                      : `0 of ${spec.total_count} candidates could be scored${spec.error ? ` — ${spec.error}` : ''}`}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowWeights(!showWeights)}
                    className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    ⚖️ Weights
                  </button>
                  <button
                    onClick={writeRationales}
                    disabled={rationaleBusy}
                    className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    title="AI-written submission rationale for the top 15"
                  >
                    {rationaleBusy ? 'Writing…' : '✨ Explain top 15'}
                  </button>
                </div>
              </div>

              {showWeights && (
                <div className="border border-gray-200 rounded-lg p-3 space-y-2 bg-gray-50">
                  {spec.criteria.map(c => (
                    <div key={c.id} className="flex items-center justify-between gap-3">
                      <span className="text-xs text-gray-700 truncate">{c.label}</span>
                      <div className="flex items-center gap-2">
                        <input
                          type="range" min={0} max={3} step={1} value={c.weight}
                          onChange={e => setWeight(c.id, parseInt(e.target.value))}
                          className="w-28 accent-orange-500"
                        />
                        <span className="text-xs w-6 text-gray-500">{c.weight === 0 ? 'Off' : c.weight}</span>
                      </div>
                    </div>
                  ))}
                  <div className="text-right flex items-center justify-end gap-2">
                    {weightsSaved === 'saved' && <span className="text-xs text-green-600 font-medium">✓ Saved</span>}
                    {weightsSaved === 'failed' && <span className="text-xs text-red-600 font-medium">Save failed — try again</span>}
                    <button
                      onClick={saveWeightsExplicit}
                      disabled={weightsSaved === 'saving'}
                      className="text-xs text-orange-600 hover:text-orange-700 font-medium disabled:opacity-50"
                    >
                      {weightsSaved === 'saving' ? 'Saving…' : 'Save weights'}
                    </button>
                  </div>
                </div>
              )}

              <div className="space-y-1.5">
                {ranked.slice(0, 50).map((r, i) => (
                  <div key={r.candidate_id} className="border border-gray-200 rounded-lg">
                    <button
                      onClick={() => setExpandedId(expandedId === r.candidate_id ? null : r.candidate_id)}
                      className="w-full px-3 py-2 flex items-center gap-3 text-left hover:bg-gray-50 rounded-lg"
                    >
                      <span className="text-xs text-gray-400 w-6">{i + 1}</span>
                      <span className={`px-2 py-0.5 rounded text-sm font-semibold ${scoreBadgeClass(r.composite)}`}>
                        {r.composite}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900 truncate">{r.name}</div>
                        <div className="text-xs text-gray-500 truncate">
                          {[r.current_position, r.current_company].filter(Boolean).join(' @ ')}
                        </div>
                      </div>
                      <span className="text-gray-300 text-xs">{expandedId === r.candidate_id ? '▲' : '▼'}</span>
                    </button>
                    {expandedId === r.candidate_id && (
                      <div className="px-3 pb-3 pt-1 border-t border-gray-100 space-y-1.5">
                        {r.rationale && (
                          <p className="text-xs text-gray-700 bg-amber-50 border border-amber-100 rounded p-2">{r.rationale}</p>
                        )}
                        {spec.criteria.filter(c => c.weight > 0).map(c => {
                          const s = r.scores[c.id];
                          const norm = s ? s.score / Math.max(1, c.levels.length - 1) : 0;
                          const levelText = s ? c.levels[Math.min(c.levels.length - 1, Math.round(s.score))] : 'No score';
                          return (
                            <div key={c.id}>
                              <div className="flex justify-between text-[11px] text-gray-600">
                                <span>{c.label}</span>
                                <span>{s ? s.score.toFixed(1) : '—'}/{c.levels.length - 1}</span>
                              </div>
                              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                  className={`h-full ${norm >= 0.7 ? 'bg-green-400' : norm >= 0.4 ? 'bg-yellow-400' : 'bg-orange-300'}`}
                                  style={{ width: `${Math.round(100 * norm)}%` }}
                                />
                              </div>
                              <div className="text-[10px] text-gray-400 truncate" title={levelText}>{levelText}</div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex justify-between items-center pt-2">
                <button onClick={() => { setView('list'); loadSpecs(); }} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">← All specs</button>
                <div className="flex gap-2">
                  <button
                    onClick={startScoring}
                    disabled={rescoreBusy}
                    className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-60"
                    title="Re-judge candidates (only new, re-enriched or changed-criteria candidates cost anything)"
                  >
                    {rescoreBusy
                      ? `⏳ Re-scoring… ${spec.status === 'scoring' && spec.total_count ? `${spec.scored_count}/${spec.total_count}` : ''}`
                      : rescoreDone ? '✓ Re-scored' : '🔄 Re-score'}
                  </button>
                  <button
                    onClick={() => { onApplyToTable(spec, ranked); onClose(); }}
                    className="px-5 py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-medium text-sm"
                  >
                    Apply to table →
                  </button>
                </div>
              </div>
            </div>
          )}

          {loading && view !== 'create' && (
            <div className="text-center py-6 text-sm text-gray-500">Loading…</div>
          )}
        </div>
      </div>
    </div>
  );
};
